import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { UserPlus, Building2, FolderKanban, Headphones, Receipt, TrendingUp, Clock, AlertTriangle, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatCurrency(byCurrency) {
  if (!byCurrency || typeof byCurrency !== "object") return "0";
  const parts = Object.entries(byCurrency).map(([c, v]) => `${Number(v).toLocaleString()} ${c}`);
  return parts.length ? parts.join(" + ") : "0";
}

function StatCard({ icon: Icon, label, value, sub, color = "text-[#F59073]" }) {
  return (
    <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <Icon className={`w-5 h-5 ${color}`} />
        <span className="font-mono text-[10px] tracking-[0.15em] text-[#857973]">{label}</span>
      </div>
      <div className="font-outfit font-bold text-3xl text-white">{value}</div>
      {sub && <div className="text-xs text-[#857973] mt-1 font-manrope">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/dashboard`, { withCredentials: true }).then(r => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-[#857973] text-sm">Loading...</div>;

  const statusColor = (s) => {
    const map = { new: "bg-blue-500/10 text-blue-400", contacted: "bg-yellow-500/10 text-yellow-400", open: "bg-green-500/10 text-green-400", in_progress: "bg-[#F59073]/10 text-[#F59073]", high: "bg-orange-500/10 text-orange-400", critical: "bg-red-500/10 text-red-400" };
    return map[s] || "bg-[#1E3050]/40 text-[#857973]";
  };

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-outfit font-bold text-2xl text-white mb-1">Dashboard</h1>
      <p className="text-sm text-[#857973] font-manrope mb-8">Business overview</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={UserPlus} label="LEADS" value={data.leads.total} sub={`${data.leads.new} new`} />
        <StatCard icon={Building2} label="CUSTOMERS" value={data.customers.total} sub={`${data.customers.active} active`} />
        <StatCard icon={FolderKanban} label="PROJECTS" value={data.projects.total} sub={`${data.projects.open} open`} />
        <StatCard icon={Headphones} label="TICKETS" value={data.tickets.open} sub="open tickets" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={TrendingUp} label="TOTAL REVENUE" value={formatCurrency(data.revenue.by_currency)} color="text-green-400" />
        <StatCard icon={Receipt} label="INVOICED THIS MONTH" value={formatCurrency(data.revenue.invoiced_this_month)} sub={`${data.revenue.invoiced_this_month_count || 0} invoices`} color="text-[#D9A872]" />
        <StatCard icon={FileText} label="AGREEMENTS MRR" value={formatCurrency(data.agreements?.mrr_by_currency)} sub="/mo" color="text-purple-400" />
        <StatCard icon={AlertTriangle} label="PENDING / OVERDUE" value={data.revenue.pending_invoices} sub={`${data.revenue.overdue_invoices} overdue`} color="text-red-400" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-medium text-white">Recent Leads</h3>
            <Link to="/admin/leads" className="text-xs text-[#F59073] hover:underline">View all</Link>
          </div>
          <div className="space-y-3">
            {data.recent_leads.length === 0 && <p className="text-sm text-[#857973]">No leads yet</p>}
            {data.recent_leads.map((lead, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[#1E3050] last:border-0">
                <div>
                  <p className="text-sm text-white font-manrope">{lead.company || lead.name}</p>
                  <p className="text-xs text-[#857973]">{lead.email}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${statusColor(lead.status)}`}>{lead.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#0F1F3A] border border-[#1E3050] rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-medium text-white">Open Tickets</h3>
            <Link to="/admin/support" className="text-xs text-[#F59073] hover:underline">View all</Link>
          </div>
          <div className="space-y-3">
            {data.recent_tickets.length === 0 && <p className="text-sm text-[#857973]">No tickets yet</p>}
            {data.recent_tickets.map((t, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[#1E3050] last:border-0">
                <div>
                  <p className="text-sm text-white font-manrope">{t.title}</p>
                  <p className="text-xs text-[#857973]">{t.priority} priority</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-sm font-mono ${statusColor(t.status)}`}>{t.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
